import React from 'react';

import { TenantFeatureFlag } from '@recurrency/core-api-schema/dist/common/enums';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { SearchPlanningDTO } from '@recurrency/core-api-schema/dist/search/getSearchPlanning';
import { SearchIndexName } from '@recurrency/core-api-schema/dist/searchIndex/common';

import { LeadTimeHistoryChartTable } from 'pages/purchasing/PlanningPage/modals/LeadTimeHistoryChartTable';

import { ActionButton } from 'components/Button/ActionButton';
import { MiniTableSkeleton } from 'components/MiniTable';
import { FailedValidationRulesPanelContent } from 'components/recipes/sidePane/FailedValidationRulesPanelContent';
import { InventoryUsagePanelContent } from 'components/recipes/sidePane/InventoryUsagePanelContent';
import { ItemLocationNotesPanelContent } from 'components/recipes/sidePane/ItemLocationNotesPanelContent';
import { ItemSettingsSection } from 'components/recipes/sidePane/ItemSettingsSection';
import { LabelValueProperties, LabelValueProperty } from 'components/recipes/sidePane/LabelValueProperties';
import { MinMaxPanelContent } from 'components/recipes/sidePane/MinMaxPanelContent';
import { OrderPointOrderQuantityPanelContent } from 'components/recipes/sidePane/OrderPointOrderQuantityPanelContent';
import { PurchaseOrderLinesPanelContent } from 'components/recipes/sidePane/PurchaseOrderLinesPanelContent';
import { SalesInvoiceLinesPanelContent } from 'components/recipes/sidePane/SalesInvoiceLinesPanelContent';
import { SalesOrderLinesPanelContext } from 'components/recipes/sidePane/SalesOrderLinesPanelContext';
import { SidePane } from 'components/recipes/sidePane/SidePane';
import { StockInheritancePanelContent } from 'components/recipes/sidePane/StockInheritancePanelContent';
import { TransferOrderLinesPanelContent } from 'components/recipes/sidePane/TransferOrderLinesPanelContent';
import { UnitOfMeasuresPanelContent } from 'components/recipes/sidePane/UnitOfMeasuresPanelContent';
import { ViewAllModal } from 'components/recipes/sidePane/ViewAllModal';

import { useGlobalApp } from 'hooks/useGlobalApp';
import { usePromise } from 'hooks/usePromise';

import { showAsyncModal } from 'utils/asyncModal';
import { captureError } from 'utils/error';
import { formatUSD, getErpName, joinIdNameObj, pluralize } from 'utils/formatting';
import { objRemoveUndefinedValues } from 'utils/object';
import { shouldShowFeatureFlag } from 'utils/roleAndTenant';
import { searchIndex } from 'utils/search/search';
import { ViewSettingKey } from 'utils/tableAndSidePaneSettings/types';
import { isTenantErpTypeNetSuite } from 'utils/tenants';
import { getTenantSetting, isHubAndSpokeEnabled, showScheduledReleaseStockFeatures } from 'utils/tenantSettings';

import { PurchaseGroupHashState } from 'types/hash-state';

import { GenericTargetLineRow } from '../../types';
import { AlternateSuppliersPanelContent } from './AlternateSuppliersPanelContent';
import { ItemNotesPanelContent } from './ItemNotesPanelContent';
import { LocationStockPanelContent } from './LocationStockPanelContent';
import { ReplenishmentPathsPanelContent } from './ReplenishmentPathsPanelContent';
import {
  ScheduledReleaseStockPanelContent,
  ScheduledReleaseStockPanelRecord,
} from './ScheduledReleaseStockPanelContent';
import { SupplierNotesPanelContent } from './SupplierNotesPanelContent';

export function ItemDetailsSidePane({
  itemInfo,
  allowMinMaxEdit = false,
  isPurchasingHubAndSpoke = false,
  onMinMaxChange,
  purchaseGroup,
  isInModal = false,
}: {
  itemInfo: GenericTargetLineRow;
  allowMinMaxEdit?: boolean;
  onMinMaxChange?: () => void;
  isPurchasingHubAndSpoke?: boolean;
  purchaseGroup?: PurchaseGroupHashState;
  isInModal?: boolean;
}) {
  const isPlanningHubAndSpoke = isHubAndSpokeEnabled();
  const aggNetStockEnabled = isPlanningHubAndSpoke && !isPurchasingHubAndSpoke && !purchaseGroup;
  const { activeTenant, activeUser } = useGlobalApp();

  const shouldShowScheduledReleaseStockFeatures = showScheduledReleaseStockFeatures();

  const { itemId, itemName: name, itemUid } = itemInfo;
  const locationId = isPurchasingHubAndSpoke ? itemInfo.purchaseLocationId : itemInfo.locationId;
  const locationName = isPurchasingHubAndSpoke ? itemInfo.purchaseLocationName : itemInfo.locationName;
  const supplierStr = joinIdNameObj({ foreignId: itemInfo.supplierId, name: itemInfo.supplierName });
  const {
    data: searchIndexResponse,
    isLoading: isPlanningIndexLoading,
    reload: reloadPlanningIndex,
  } = usePromise(
    () =>
      searchIndex<SearchPlanningDTO>({
        indexName: SearchIndexName.Planning,
        filters: objRemoveUndefinedValues({
          ...(locationId && locationName
            ? // Don't trim location name as some locations have leading/trailing spaces
              { location: [joinIdNameObj({ foreignId: locationId, name: locationName }, true)] }
            : {}),
          item_id: [itemId],
        }),
        hitsPerPage: 1,
      }),
    [itemId, locationId],
    {
      cacheKey: `search:${JSON.stringify([SearchIndexName.Planning, itemId, locationId])}`,
      onError: captureError,
    },
  );

  const minMaxRecord = searchIndexResponse?.hits?.[0];
  // hide item notes panel for netsuite since we don't sync item notes for netsuite tenants);
  const showItemNotesPanel = !isTenantErpTypeNetSuite(activeTenant.erpType);
  const showStockInheritance = !!getTenantSetting(TenantSettingKey.FeatureStockInheritance);

  const detailProps: Array<LabelValueProperty | null> = [
    minMaxRecord?.short_code ? { label: 'Short Code', value: minMaxRecord?.short_code } : null,
    minMaxRecord?.item_group ? { label: 'Product Group', value: minMaxRecord?.item_group } : null,
    itemInfo.supplierPartId ? { label: 'Supplier Part ID', value: itemInfo.supplierPartId } : null,
    minMaxRecord?.lead_time
      ? {
          label: 'Lead Time',
          value: (
            <>
              {pluralize(minMaxRecord.lead_time, 'day', 'days', true)}
              {` | `}
              <ActionButton
                label="View History"
                onClick={() =>
                  showAsyncModal(ViewAllModal, {
                    title: `Lead Time History for Item #${minMaxRecord.item_id} at Location ${minMaxRecord.location}`,
                    content: <LeadTimeHistoryChartTable record={minMaxRecord} />,
                  })
                }
              />
            </>
          ),
        }
      : null,
    itemInfo.unitCost ? { label: 'System Unit Cost', value: formatUSD(itemInfo.unitCost, true) } : null,
  ];

  const isOpOqItem = minMaxRecord?.replenishment_method === 'OP/OQ';
  const itemLocations =
    isPurchasingHubAndSpoke && purchaseGroup
      ? [
          { foreignId: purchaseGroup.purchaseLocationId, name: purchaseGroup.purchaseLocationName },
          ...(purchaseGroup.spokeLocations ?? []),
        ]
      : [
          {
            foreignId: itemInfo.locationId as string,
            name: itemInfo.locationName as string,
          },
        ];

  const itemLocationIds = itemLocations.map((i) => i.foreignId);

  return (
    <SidePane
      offsetTop={isInModal ? '0px' : '120px'}
      title={`Item #${itemInfo.itemId} at ${
        isPurchasingHubAndSpoke ? itemInfo.groupName : `${itemInfo.locationId}: ${itemInfo.locationName}`
      }`}
      settingKey={ViewSettingKey.PurchaseTargetItemDetailsSidePane}
      description={itemInfo.itemName}
      extendedDescription={itemInfo.itemDescription}
      content={
        isPlanningIndexLoading ? (
          <MiniTableSkeleton />
        ) : (
          <>
            <LabelValueProperties properties={detailProps} />
            <ItemSettingsSection itemUid={itemUid} itemCode={itemId} onSaved={reloadPlanningIndex} />
          </>
        )
      }
      panels={[
        // LocationId is null if this is used in the context of a purchase group. AggNetStockEnabled checks for purchase group
        // but locationId is left in to appease typsecript. Logically, the locationId condition can be ignored
        aggNetStockEnabled && locationId
          ? {
              settingKey: 'replenishmentPath',
              title: 'Replenishment Path',
              content: (
                <ReplenishmentPathsPanelContent
                  itemUid={itemUid}
                  itemId={itemId}
                  locationId={locationId}
                  isPurchasing
                />
              ),
            }
          : null,
        {
          settingKey: 'usage',
          title: 'Usage',
          content: (
            <InventoryUsagePanelContent
              itemId={itemId}
              itemUid={itemUid}
              locations={itemLocations}
              purchaseGroupName={purchaseGroup?.groupName}
            />
          ),
        },
        showStockInheritance
          ? {
              settingKey: 'stockInheritance',
              title: 'Stock Inheritance',
              content: <StockInheritancePanelContent itemId={itemId} itemUid={itemUid} />,
            }
          : null,
        !isPurchasingHubAndSpoke
          ? {
              settingKey: 'opOqMinMax',
              title: isOpOqItem ? 'Order Point / Order Quantity' : 'Min / Max',
              content: isPlanningIndexLoading ? (
                <MiniTableSkeleton />
              ) : isOpOqItem ? (
                <OrderPointOrderQuantityPanelContent
                  minMaxRecord={minMaxRecord}
                  itemInfo={itemInfo}
                  allowOpOqEdit={allowMinMaxEdit}
                  onOpOqChange={() => {
                    reloadPlanningIndex();
                    onMinMaxChange?.();
                  }}
                />
              ) : (
                <MinMaxPanelContent
                  minMaxRecord={minMaxRecord}
                  itemInfo={itemInfo}
                  allowMinMaxEdit={allowMinMaxEdit}
                  onMinMaxChange={() => {
                    reloadPlanningIndex();
                    onMinMaxChange?.();
                  }}
                />
              ),
            }
          : null,
        showItemNotesPanel
          ? {
              settingKey: 'itemNotes',
              title: `${getErpName(activeTenant.erpType)} Item Notes`,
              content: <ItemNotesPanelContent uniqueItems={[{ itemId: itemUid, foreignId: itemId, name }]} />,
            }
          : null,
        {
          settingKey: 'itemLocationNotes',
          title: 'Recurrency Notes',
          content: (
            <ItemLocationNotesPanelContent
              itemLocationTitle={`${name} at ${locationName}`}
              itemUid={itemUid}
              locationId={itemInfo.locationId as string}
            />
          ),
          optional: true,
        },
        {
          settingKey: 'locationStock',
          title: 'Location Stock',
          content: <LocationStockPanelContent targetLine={itemInfo} />,
        },
        // Always exactly one item at one location. In hub-and-spoke purchasing this is the purchase (hub) location,
        // where the PO is placed, same as the Transfer Orders panel above. Skipped when there is no location.
        locationId && shouldShowFeatureFlag(activeTenant, activeUser, TenantFeatureFlag.FeatureAlternateSuppliersPanel)
          ? {
              settingKey: 'alternateSuppliers',
              title: 'Alternate Suppliers',
              content: <AlternateSuppliersPanelContent itemUid={itemUid} locationId={locationId} />,
            }
          : null,
        {
          settingKey: 'unitOfMeasures',
          title: 'Unit of Measures',
          content: <UnitOfMeasuresPanelContent itemId={itemInfo.itemId} />,
        },
        {
          settingKey: 'purchaseOrders',
          title: 'Purchase Orders',
          content: (
            <PurchaseOrderLinesPanelContent
              itemId={itemId}
              companyId={itemInfo.companyId}
              locationIds={itemLocationIds}
            />
          ),
        },
        locationId
          ? {
              settingKey: 'transferOrders',
              title: 'Transfer Orders',
              content: (
                <TransferOrderLinesPanelContent
                  itemId={itemUid}
                  itemCode={itemId}
                  locationId={locationId}
                  locationName={itemInfo.locationName ?? ''}
                />
              ),
            }
          : null,
        {
          settingKey: 'salesOrders',
          title: 'Sales Orders',
          content: (
            <SalesOrderLinesPanelContext itemId={itemId} locationIds={itemLocationIds} companyId={itemInfo.companyId} />
          ),
        },
        {
          settingKey: 'salesHistory',
          title: 'Sales History',
          content: (
            <SalesInvoiceLinesPanelContent
              itemId={itemId}
              locationIds={itemLocationIds}
              companyId={itemInfo.companyId}
            />
          ),
        },
        shouldShowScheduledReleaseStockFeatures
          ? {
              settingKey: 'scheduledReleases',
              title: 'Open Scheduled Releases',
              content: <ScheduledReleaseStockPanelContent itemInfo={itemInfo as ScheduledReleaseStockPanelRecord} />,
            }
          : null,
        {
          settingKey: 'supplierNotes',
          title: `${getErpName(activeTenant.erpType)} Supplier Notes`,
          content: <SupplierNotesPanelContent uniqueSuppliers={[supplierStr]} />,
        },
        {
          settingKey: 'failedValidationRules',
          title: 'Failed Rules',
          content: <FailedValidationRulesPanelContent itemUid={itemUid} locationId={itemLocationIds[0]} />,
        },
      ].filter(Boolean)}
    />
  );
}
